/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Container,
  Typography,
  Card,
  Grid,
  Switch,
  Button,
  Chip,
  alpha,
  useTheme,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  MenuItem
} from '@mui/material';
import {
  CreditCard as StripeIcon,
  CurrencyBitcoin as BinanceIcon,
  AccountBalanceWallet as ZelleIcon,
  PhoneIphone as PagoMovilIcon,
  AccountBalance as BankIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getApi, putApi, patchApi } from '../services/api';

// ---------------------------------------------------------------------------
// Definición de campos de configuración por gateway. El orden importa: se
// renderizan en el mismo orden dentro del modal de configuración.
// ---------------------------------------------------------------------------
const FIELD_DEFS = {
  stripe: [
    { key: 'publishableKey', label: 'Publishable Key (pk_...)', type: 'text', required: true },
    { key: 'secretKey', label: 'Secret Key (sk_...)', type: 'password', required: true },
    { key: 'webhookSecret', label: 'Webhook Secret (opcional)', type: 'password' },
    {
      key: 'currency',
      label: 'Moneda',
      type: 'select',
      options: [
        { value: 'usd', label: 'USD' },
        { value: 'eur', label: 'EUR' },
        { value: 'mxn', label: 'MXN' },
        { value: 'cop', label: 'COP' }
      ]
    }
  ],
  binance: [
    { key: 'walletAddress', label: 'Dirección de Wallet (USDT)', type: 'text', required: true },
    {
      key: 'network',
      label: 'Red',
      type: 'select',
      options: [
        { value: 'BEP20', label: 'BEP20 (BSC)' },
        { value: 'TRC20', label: 'TRC20 (Tron)' },
        { value: 'ERC20', label: 'ERC20 (Ethereum)' }
      ]
    },
    { key: 'qrImageUrl', label: 'URL del QR (opcional)', type: 'text' }
  ],
  zelle: [
    { key: 'email', label: 'Email Zelle', type: 'text', required: true },
    { key: 'recipientName', label: 'Nombre del beneficiario', type: 'text', required: true }
  ],
  pago_movil: [
    { key: 'bank', label: 'Banco', type: 'text', required: true },
    { key: 'phone', label: 'Teléfono', type: 'text', required: true },
    { key: 'idNumber', label: 'Cédula / RIF', type: 'text', required: true },
    { key: 'accountHolder', label: 'Titular', type: 'text' }
  ],
  bank_transfer: [
    { key: 'bankName', label: 'Nombre del banco', type: 'text', required: true },
    { key: 'accountNumber', label: 'Número de cuenta', type: 'text', required: true },
    { key: 'accountType', label: 'Tipo de cuenta', type: 'text' },
    { key: 'accountHolder', label: 'Titular', type: 'text', required: true },
    { key: 'routingNumber', label: 'Routing / ABA (US)', type: 'text' },
    { key: 'swiftCode', label: 'SWIFT / BIC', type: 'text' }
  ]
};

const ICON_BY_CODE = {
  stripe: { Icon: StripeIcon, color: '#635BFF' },
  binance: { Icon: BinanceIcon, color: '#F0B90B' },
  zelle: { Icon: ZelleIcon, color: '#6D1ED4' },
  pago_movil: { Icon: PagoMovilIcon, color: '#26A69A' },
  bank_transfer: { Icon: BankIcon, color: '#1E88E5' }
};

const AdminPaymentGateways = () => {
  const theme = useTheme();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // gateway en edición
  const [formConfig, setFormConfig] = useState({});
  const [formInstructions, setFormInstructions] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchGateways = async () => {
    setLoading(true);
    try {
      const res = await getApi('api/payment-gateways');
      if (res?.status === 200) setGateways(res.data?.gateways || []);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar las pasarelas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  const handleToggle = async (gateway, value) => {
    try {
      const res = await patchApi(`api/payment-gateways/${gateway.code}/toggle`, { enabled: value });
      if (res?.status === 200) {
        toast.success(`${gateway.name} ${value ? 'habilitado' : 'deshabilitado'}`);
        fetchGateways();
      } else {
        toast.error('No se pudo actualizar el estado');
      }
    } catch (e) {
      toast.error('No se pudo actualizar el estado');
    }
  };

  const handleOpenEdit = (gateway) => {
    setEditing(gateway);
    setFormConfig({ ...(gateway.config || {}) });
    setFormInstructions(gateway.instructions || '');
  };

  const handleCloseEdit = () => {
    setEditing(null);
    setFormConfig({});
    setFormInstructions('');
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await putApi(`api/payment-gateways/${editing.code}`, {
        config: formConfig,
        instructions: formInstructions
      });
      if (res?.status === 200) {
        toast.success('Configuración guardada');
        await fetchGateways();
        handleCloseEdit();
      } else {
        toast.error('No se pudo guardar la configuración');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const enabled = gateways.filter((g) => g.enabled).length;
    return { total: gateways.length, enabled, disabled: gateways.length - enabled };
  }, [gateways]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          borderRadius: 3
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Pasarelas de Pago
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Activa, desactiva y configura los métodos de pago disponibles para los hoteles
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AdminIcon sx={{ fontSize: 60, opacity: 0.3 }} />
          </Box>
        </Stack>
      </Paper>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SettingsIcon sx={{ color: theme.palette.primary.main }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pasarelas Disponibles
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SettingsIcon sx={{ color: theme.palette.success.main }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {stats.enabled}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Habilitadas
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SettingsIcon sx={{ color: theme.palette.error.main }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} color="error">
                  {stats.disabled}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deshabilitadas
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Gateway cards */}
      {loading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={3}>
          {gateways.map((g) => {
            const meta = ICON_BY_CODE[g.code] || { Icon: SettingsIcon, color: theme.palette.primary.main };
            const Icon = meta.Icon;
            return (
              <Grid item xs={12} md={6} key={g.code}>
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${alpha(g.enabled ? meta.color : theme.palette.divider, 0.5)}`,
                    transition: 'all 0.3s'
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: alpha(meta.color, 0.12),
                        color: meta.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box flex={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            {g.name}
                          </Typography>
                          <Stack direction="row" spacing={1} mt={0.5}>
                            <Chip
                              size="small"
                              label={g.type === 'automatic' ? 'Automático' : 'Manual'}
                              color={g.type === 'automatic' ? 'primary' : 'default'}
                            />
                            <Chip
                              size="small"
                              label={g.enabled ? 'Activo' : 'Inactivo'}
                              color={g.enabled ? 'success' : 'default'}
                            />
                          </Stack>
                        </Box>
                        <Switch
                          checked={!!g.enabled}
                          onChange={(e) => handleToggle(g, e.target.checked)}
                          color="success"
                        />
                      </Stack>

                      {g.instructions && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                          {g.instructions}
                        </Typography>
                      )}

                      <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleOpenEdit(g)}
                        size="small"
                      >
                        Configurar
                      </Button>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit modal */}
      <Dialog open={!!editing} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configurar {editing?.name}
          <IconButton onClick={handleCloseEdit} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {(FIELD_DEFS[editing?.code] || []).map((f) => {
              if (f.type === 'select') {
                return (
                  <TextField
                    key={f.key}
                    select
                    fullWidth
                    size="small"
                    label={f.label}
                    value={formConfig[f.key] || ''}
                    onChange={(e) => setFormConfig({ ...formConfig, [f.key]: e.target.value })}
                  >
                    {f.options.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }
              return (
                <TextField
                  key={f.key}
                  fullWidth
                  size="small"
                  type={f.type}
                  label={f.label + (f.required ? ' *' : '')}
                  value={formConfig[f.key] || ''}
                  onChange={(e) => setFormConfig({ ...formConfig, [f.key]: e.target.value })}
                />
              );
            })}
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="Instrucciones para el hotel"
              value={formInstructions}
              onChange={(e) => setFormInstructions(e.target.value)}
              helperText="Texto que verá el hotel en el modal de pago al elegir este método."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Guardar configuración
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPaymentGateways;
